import type { MobileFrontEndGalleryView } from '@/shared/mobile-service/api/cors-mobile-api';

/** Media kind resolved for one tile or message attachment. */
export type GalleryMediaKind = 'image' | 'video' | 'audio' | 'document';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tiff'];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi', 'mkv', 'webm', '3gp'];
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'aac', 'wav', 'ogg', 'opus', 'amr', 'flac'];

/**
 * Decide how to render one file.
 *
 * The declared `media_kind` wins when the artifact only ever holds one kind.
 * Otherwise MIME is authoritative and the file extension is the fallback, since
 * plenty of extractions record a path with no MIME at all.
 */
export function resolveMediaKind(
  declared: MobileFrontEndGalleryView['media_kind'],
  mime: string | null,
  url: string,
): GalleryMediaKind {
  if (declared && declared !== 'auto') return declared;

  const type = mime?.toLowerCase() ?? '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';

  const extension = fileExtension(url);
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (VIDEO_EXTENSIONS.includes(extension)) return 'video';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';
  return 'document';
}

/** Fetchable media URL, or null for a device-local path the browser cannot load. */
export function displayableMediaUrl(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  // Extractions sometimes record a device-local path rather than a fetchable
  // object; those can't be rendered and shouldn't become broken tiles.
  // mobile-service collected files use ``s3://``; browser display goes through
  // the same-origin proxy via ``storageUrlForDisplay``.
  return /^(https?:|data:|blob:|s3:)/i.test(text) ? text : null;
}

/**
 * Formats an ``<img>`` or ``<video>`` cannot decode.
 *
 * Same set the evidence viewer treats as needing a provenance transcode
 * (HEIC/HEIF/TIFF, and non-native video such as AVI/MKV).
 */
const TRANSCODE_IMAGE_EXTENSIONS = new Set(['heic', 'heif', 'tif', 'tiff']);
const TRANSCODE_VIDEO_EXTENSIONS = new Set(['avi', 'mkv', 'flv', 'wmv', 'mpeg', 'mpg', 'dav']);
const TRANSCODE_IMAGE_MIMES = new Set(['image/heic', 'image/heif', 'image/tiff', 'image/tif']);

export function fileExtension(url: string): string {
  const path = url.split(/[?#]/)[0];
  const name = path.split('/').pop() ?? '';
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

/** True when the browser needs a webp/mp4 preview instead of the original bytes. */
export function mediaNeedsTranscode(
  kind: GalleryMediaKind,
  mime: string | null,
  url: string,
): boolean {
  const extension = fileExtension(url);
  const type = mime?.split(';')[0]?.trim().toLowerCase() ?? '';
  if (kind === 'image') {
    return TRANSCODE_IMAGE_EXTENSIONS.has(extension) || TRANSCODE_IMAGE_MIMES.has(type);
  }
  if (kind === 'video') return TRANSCODE_VIDEO_EXTENSIONS.has(extension);
  return false;
}
